import { ArrowUpRight } from "lucide-react";
import { BOOK_CALL_URL, CONTACT_EMAIL, EMAIL_URL, WHATSAPP_URL } from "@/lib/site";
import { track } from "@/lib/track";

const LINKS = [
    { label: "Services", href: "#services", id: "services" },
    { label: "Our Work", href: "#work", id: "work" },
    { label: "Solutions", href: "#solutions", id: "solutions" },
    { label: "Process", href: "#process", id: "process" },
    { label: "Contact", href: "#contact", id: "contact" },
];

const SERVICE_NAMES = [
    "AI Strategy & Consulting",
    "AI Workflow Automation",
    "Custom Websites & Web Applications",
    "AI-Powered Business Solutions",
    "Process Optimization & Digital Transformation",
    "White-Label AI Development",
];

const HEADING = "text-xs font-bold uppercase tracking-widest text-[#A89E95]";
const FLINK =
    "text-sm text-[#57504A] transition-colors duration-200 hover:text-[#1C1917]";

export default function Footer() {
    const externalCall = BOOK_CALL_URL.startsWith("http");
    return (
        <footer className="border-t border-[#EDE3DA] bg-[#FDF7F2]">
            <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
                <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <a
                            href="#top"
                            data-testid="footer-brand"
                            className="inline-flex items-center gap-2.5"
                            aria-label="StackDino — back to top"
                        >
                            <img
                                src="/assets/stackdino-mark.png"
                                alt=""
                                width={407}
                                height={407}
                                className="h-8 w-8 rounded-lg"
                            />
                            <span className="font-heading text-base font-semibold text-[#1C1917]">
                                StackDino
                            </span>
                        </a>
                        <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#57504A]">
                            Premium websites, custom web applications and AI-powered workflows
                            for businesses and agencies across Asia.
                        </p>
                    </div>

                    <nav aria-label="Footer">
                        <p className={HEADING}>Explore</p>
                        <div className="mt-4 flex flex-col gap-2.5">
                            {LINKS.map((l) => (
                                <a
                                    key={l.id}
                                    href={l.href}
                                    data-testid={`footer-${l.id}-link`}
                                    className={FLINK}
                                >
                                    {l.label}
                                </a>
                            ))}
                        </div>
                    </nav>

                    <div>
                        <p className={HEADING}>Services</p>
                        <div className="mt-4 flex flex-col gap-2.5">
                            {SERVICE_NAMES.map((s) => (
                                <a
                                    key={s}
                                    href="#services"
                                    data-testid={`footer-service-${s
                                        .toLowerCase()
                                        .replace(/[^a-z]+/g, "-")}-link`}
                                    className={FLINK}
                                >
                                    {s}
                                </a>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className={HEADING}>Contact</p>
                        <div className="mt-4 flex flex-col gap-2.5">
                            {EMAIL_URL && (
                                <a
                                    href={EMAIL_URL}
                                    data-testid="footer-email-link"
                                    className={FLINK}
                                >
                                    {CONTACT_EMAIL}
                                </a>
                            )}
                            {WHATSAPP_URL && (
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-whatsapp-link"
                                    className={FLINK}
                                >
                                    WhatsApp
                                </a>
                            )}
                            <a
                                href={BOOK_CALL_URL}
                                target={externalCall ? "_blank" : undefined}
                                rel={externalCall ? "noopener noreferrer" : undefined}
                                data-testid="footer-book-call-link"
                                onClick={() => track("cta_click", { cta: "footer_book_call" })}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#CF4242] transition-colors duration-200 hover:text-[#A92F31]"
                            >
                                Book a free consultancy call
                                <ArrowUpRight size={14} aria-hidden="true" />
                            </a>
                        </div>
                    </div>
                </div>

                <div className="mt-14 flex flex-col gap-3 border-t border-[#EDE3DA] pt-6 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-[#A89E95]">
                        © {new Date().getFullYear()} StackDino. All rights reserved.
                    </p>
                    <p className="text-xs text-[#A89E95]">
                        Premium websites, web apps & AI workflows — built across Asia.
                    </p>
                </div>
            </div>
        </footer>
    );
}
